import {
    Box,
    Divider,
    FormControl,
    FormErrorMessage,
    FormLabel,
    RadioAddon,
    Stack,
    ThemeBorderVariable,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeRadiusVariable,
    ThemeSpaceVariable,
    useRadioGroup,
} from "@caspeco/casper-ui-library";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { useBookingActionsContext } from "hooks/use-booking-state";
import { t } from "i18next";
import { memo } from "react";
import {
    Control,
    Controller,
    ControllerFieldState,
    ControllerRenderProps,
    UseFormTrigger,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ContactInfoForm } from "store/store-types";
import { PaymentMethod } from "types/payment";
import { PaymentMethodLabel } from "./payment-label";

interface InnerField {
    options: PaymentMethod[];
    field: ControllerRenderProps<ContactInfoForm, "payMethod">;
    fieldState: ControllerFieldState;
    trigger: UseFormTrigger<ContactInfoForm>;
}

const InnerField = memo(({ options, field, fieldState, trigger }: InnerField) => {
    const { t } = useTranslation();
    const { setPaymentMethod } = useBookingActionsContext();

    const onChange = (nextValue: string) => {
        field.onChange(nextValue);
        setPaymentMethod?.(parseInt(nextValue));
        void trigger("payMethod");
    };

    const { getRootProps, getRadioProps } = useRadioGroup({
        name: field.name,
        value: field.value?.toString(),
        onChange,
    });
    const { ...radioGroupProps } = getRootProps();
    return (
        <FormControl isInvalid={Boolean(fieldState.error)}>
            <FormLabel
                color={ThemeColorVariable.OnBackgroundHeader}
                fontSize={ThemeFontSizeVariable.Large}
                fontFamily={ThemeFontVariable.SubHeader}
                fontWeight="medium"
            >
                {t("paymentMethods.title")}
            </FormLabel>
            <Box
                border={ThemeBorderVariable.XSmall}
                borderColor={ThemeColorVariable.OnSurfaceBorder}
                borderRadius={ThemeRadiusVariable.Small}
                bg={ThemeColorVariable.Surface}
            >
                <Stack
                    py={ThemeSpaceVariable.Small}
                    spacing={ThemeSpaceVariable.Small}
                    divider={
                        <Divider
                            my={ThemeSpaceVariable.Medium}
                            borderColor={ThemeColorVariable.OnSurfaceBorder}
                        />
                    }
                    {...radioGroupProps}
                >
                    {options.map((option, index) => {
                        const radioProps = getRadioProps({
                            value: JSON.stringify(option),
                        });
                        return (
                            <RadioAddon
                                key={index}
                                id={option.toString()}
                                px={ThemeSpaceVariable.Medium}
                                flexDirection="row-reverse"
                                justifyContent="space-between"
                                {...radioProps}
                            >
                                <PaymentMethodLabel paymentMethod={option} />
                            </RadioAddon>
                        );
                    })}
                </Stack>
            </Box>
            <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
        </FormControl>
    );
});

InnerField.displayName = "InnerField";

export function PaymentMethodField({
    control,
    trigger,
}: {
    control: Control<ContactInfoForm, string>;
    trigger: UseFormTrigger<ContactInfoForm>;
}) {
    const { data: settings } = useExternalBookingSettings();
    if (!settings) return null;
    const availablePaymentMethods = settings.paymentMethods ?? [];

    return (
        <Controller
            name="payMethod"
            control={control}
            rules={{
                required: {
                    value: true,
                    message: t("enterPaymentMethod"),
                },
            }}
            render={({ field, fieldState }) => (
                <InnerField
                    options={availablePaymentMethods}
                    field={field}
                    fieldState={fieldState}
                    trigger={trigger}
                />
            )}
        />
    );
}
